import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'Find Your Home',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);

  return (
    <section className="relative w-full h-[68vh] md:h-[85vh] min-h-[380px] md:min-h-[560px] max-h-[900px] overflow-hidden">
      {/* Static background image */}
      <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
        <div className="absolute inset-0">
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              loading="eager"
              fetchPriority="high"
              decoding="async"
              className="w-full h-full object-cover"
              style={{ imageRendering: 'high-quality' as any }}
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      {/* Gradient overlay for text contrast */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6 sm:px-8">
        <RichTextContent
          path="hero.pre_title"
          label="Pre-Title"
          defaultValue='<p>Welcome to</p>'
          as="p"
          className="hero-entrance hero-entrance-1 font-body text-sm sm:text-base md:text-lg font-bold tracking-[0.25em] uppercase opacity-80 mb-2 drop-shadow-md"
        />
        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue='<p><span data-type="field-token" data-field="property_name"></span></p>'
          as="h1"
          className="hero-entrance hero-entrance-2 font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-10 max-w-5xl leading-[1.1] tracking-tight uppercase"
          style={{ color: '#fff' }}
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaLink.url || '/floor-plans'}
            target={ctaLink._target || '_self'}
            className="hero-entrance hero-entrance-4 hero-cta group/cta relative inline-flex items-center gap-2.5 px-10 py-3.5 font-heading text-base font-medium tracking-[0.15em] uppercase overflow-hidden transition-all duration-300"
          >
            <span className="relative z-10">{ctaLink.label || 'Find Your Home'}</span>
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.1s; }
        .hero-entrance-2 { animation-delay: 0.3s; }
        .hero-entrance-3 { animation-delay: 0.55s; }
        .hero-entrance-4 { animation-delay: 0.8s; }
        /* Hero CTA palette: primary fill at rest, secondary on hover —
           the inverse of the header's Schedule-a-Tour pill so the two
           main conversion buttons on the home page read as a
           complementary pair (hero leads with primary, header leads with
           secondary). Border tracks the fill so the outline never
           lingers visibly behind the new background mid-transition. */
        .hero-cta {
          background: var(--vibe-color-primary);
          border: 1.5px solid var(--vibe-color-primary);
          border-radius: 2px;
          color: var(--vibe-color-text-on-primary, #fff);
          box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
          transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease, box-shadow 0.3s ease, transform 0.3s ease;
        }
        .hero-cta:hover {
          background: var(--vibe-color-secondary);
          border-color: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary, #fff);
          box-shadow: 0 10px 24px rgba(0, 0, 0, 0.3);
          transform: translateY(-2px);
        }
        .hero-cta:active {
          transform: translateY(0);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.pre_title': {
      label: 'Pre-Title',
      type: 'richtext',
      defaultValue: '<p>Welcome to</p>',
    },
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.cta_link': ctaLinkDef,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
